import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Bid {
  id: string;
  order_id: string;
  transporter_user_id: string;
  transporter_company_id: string;
  amount: number;
  message: string | null;
  is_accepted: boolean;
  declined_at: string | null;
  created_at: string;
  transporter_company?: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
}

export interface CreateBidData {
  order_id: string;
  amount: number;
  message?: string;
}

export const useBids = (orderId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch bids for a specific order
  const { data: bids, isLoading } = useQuery({
    queryKey: ["bids", orderId],
    queryFn: async () => {
      if (!orderId) return [];

      const { data, error } = await supabase
        .from("bids")
        .select(`
          *,
          transporter_company:companies!bids_transporter_company_id_fkey(
            id,
            name,
            email,
            phone
          )
        `)
        .eq("order_id", orderId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Bid[];
    },
    enabled: !!orderId,
  });

  // Create a new bid
  const createBidMutation = useMutation({
    mutationFn: async (bidData: CreateBidData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("company_id")
        .eq("id", user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.company_id) throw new Error("User has no company");

      // Vérifier qu'il n'existe pas déjà une offre acceptée pour cette commande
      const { data: existingAcceptedBid } = await supabase
        .from("bids")
        .select("id")
        .eq("order_id", bidData.order_id)
        .eq("is_accepted", true)
        .single();

      if (existingAcceptedBid) {
        throw new Error("Cette commande a déjà une offre acceptée");
      }

      // Supprimer toute offre existante non acceptée (is_accepted = false OU NULL)
      const { error: deleteError } = await supabase
        .from("bids")
        .delete()
        .eq("order_id", bidData.order_id)
        .eq("transporter_company_id", userData.company_id)
        .or("is_accepted.is.null,is_accepted.eq.false");

      if (deleteError) {
        console.error("Error deleting old bid:", deleteError);
        throw new Error("Impossible de supprimer l'offre existante");
      }

      const { data, error } = await supabase
        .from("bids")
        .insert({
          order_id: bidData.order_id,
          transporter_user_id: user.id,
          transporter_company_id: userData.company_id,
          amount: bidData.amount,
          message: bidData.message || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Offre soumise",
        description: "Votre offre a été envoyée avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ["bids"] });
      queryClient.invalidateQueries({ queryKey: ["my-bids"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Accept a bid
  const acceptBidMutation = useMutation({
    mutationFn: async (bidId: string) => {
      // Get bid details first
      const { data: bid, error: bidError } = await supabase
        .from("bids")
        .select("*, order_id, amount, transporter_company_id")
        .eq("id", bidId)
        .single();

      if (bidError) throw bidError;

      // Update bid as accepted
      const { error: updateBidError } = await supabase
        .from("bids")
        .update({ is_accepted: true })
        .eq("id", bidId);

      if (updateBidError) throw updateBidError;

      // Decline all other bids for this order
      const { error: declineOthersError } = await supabase
        .from("bids")
        .update({ declined_at: new Date().toISOString() })
        .eq("order_id", bid.order_id)
        .neq("id", bidId)
        .is("is_accepted", false)
        .is("declined_at", null);

      if (declineOthersError) throw declineOthersError;

      // Update order status and final price
      const { error: updateOrderError } = await supabase
        .from("orders")
        .update({
          status: "booked",
          final_price: bid.amount,
        })
        .eq("id", bid.order_id);

      if (updateOrderError) throw updateOrderError;

      // Create transport_job for the transporter
      const { error: createJobError } = await supabase
        .from("transport_jobs")
        .insert({
          order_id: bid.order_id,
          transporter_company_id: bid.transporter_company_id,
          booked_at: new Date().toISOString(),
          accepted_at: new Date().toISOString(), // Déjà accepté par le shipper
        });

      if (createJobError) throw createJobError;

      return bid;
    },
    onSuccess: () => {
      toast({
        title: "Offre acceptée",
        description: "L'offre a été acceptée avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ["bids"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["transport-jobs"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Decline a bid
  const declineBidMutation = useMutation({
    mutationFn: async (bidId: string) => {
      const { error } = await supabase
        .from("bids")
        .update({ declined_at: new Date().toISOString() })
        .eq("id", bidId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Offre refusée",
        description: "L'offre a été refusée avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ["bids"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    bids,
    isLoading,
    createBid: createBidMutation.mutateAsync,
    acceptBid: acceptBidMutation.mutateAsync,
    declineBid: declineBidMutation.mutateAsync,
    isCreating: createBidMutation.isPending,
    isAccepting: acceptBidMutation.isPending,
    isDeclining: declineBidMutation.isPending,
  };
};
